import { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowBigRight, BarChart2, Clock, Edit, FoldHorizontal, FoldVertical, Hamburger, LetterText, MapIcon, Pentagon, ScrollText, Settings, Shrink } from 'lucide-react';
import { Widget } from './components/Widget.tsx';
import { SettingsPanel } from './components/SettingsPanel.tsx'
import { useLocalStorage } from './hooks/useLocalStorage.ts';
import { HomeScreenConfig, Position, WidgetInterface } from './types';
import { ClockWidget } from './components/WidgetTypes/ClockWidget.tsx';
import './style.css'
import TrafficStatus from './components/WidgetTypes/TrafficStatus.tsx';
import VerticalSeparator from './components/WidgetTypes/VerticalSeparator.tsx';
import HorizontalSeparator from './components/WidgetTypes/HorizontalSeparator.tsx';
import FoodWidget from './components/WidgetTypes/FoodWidget.tsx';
import { LogoWidget } from './components/WidgetTypes/LogoWidget.tsx';
import TrafficGraph from './components/WidgetTypes/TrafficGraph.tsx';
import { MarqueeWidget } from './components/WidgetTypes/MarqueeWidget.tsx';
import { MapWidget } from './components/WidgetTypes/MapWidget.tsx';
import { TitleWidget } from './components/WidgetTypes/TitleWidget.tsx';


/* Default Initial Config: */
const defaultConfig: HomeScreenConfig = {
  textColor: {
    color: "#ffffff",
    secondaryColor: "#888"
  },
  background: {
    type: 'color',
    value: '#1a2235'
  },
  widgets: [],
  snapValue: 16,
  antiBurnJitter: 8,
  stages: 1,
  stageSwitchTime: 3000
};

function forceToWidgetScreen(position : Position) : Position {
    return {x: Math.min(Math.max(0, position.x ), window.innerWidth-50), y: Math.min(Math.max(-45, position.y), window.innerHeight-50)}
}

const contextMenuWidth = 500;
const rng = (n : number) => {return (Math.sin(2*n) * Math.sin(Math.PI*n)) / 2.0;}
var rn = 1;

function App() {
  const [config, setConfig] = useLocalStorage<HomeScreenConfig>('homescreen-config', defaultConfig);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState< { x: number; y: number;} >( {x: rng(rn+100)-0.5, y: rng(rn)-0.5} );
  const [screenAntiBurnJitter, setScreenAntiBurnJitter] = useState< { x: number; y: number;} >( {x: 0, y: 0} );
  const contextMenuRef = useRef<HTMLDivElement|null>(null);
  const [stage, setStage] = useState(1);

  const keyDownHandle = (e : KeyboardEvent) => {
    if (e.ctrlKey && e.key == "e") {
      setEditMode(!editMode);
    }
  }

  const mouseDownHandle = () => {
    setShowContextMenu(false);
  }

  const updateStage = () => {
    if(!editMode)
      setStage((stage % config.stages) + 1)
  }
  
  const updateAntiburnJitter = () => {
    rn+=0.01;
    setScreenAntiBurnJitter({x: rng(rn+100)-0.5, y: rng(rn)-0.5});
  }

  useEffect(() => {
      document.addEventListener("keydown", keyDownHandle);
      document.addEventListener("click", mouseDownHandle);
      const jitter_interval_i = setTimeout(updateAntiburnJitter, 100)
      const jitter_interval = setInterval(updateAntiburnJitter, config.antiBurnJitter > 50 ? 10 : 10000);
      const stage_interval = config.stageSwitchTime ? setInterval(updateStage, config.stageSwitchTime) : null;
      return () => {
        document.removeEventListener("keydown", keyDownHandle); 
        document.removeEventListener("click", mouseDownHandle);
        clearInterval(jitter_interval);
        stage_interval ? clearInterval(stage_interval) : null;
        clearTimeout(jitter_interval_i)
      }

  }, [config, editMode, stage])

  const updateWidget = useCallback((id: string, updates: Partial<WidgetInterface>) => {
    setConfig(prev => ({
      ...prev,
      widgets: prev.widgets.map(widget =>
        widget.id === id ? { ...widget, ...updates } : widget
      )
    }));
  }, [setConfig]);

  const deleteWidget = useCallback((id: string) => {
    setConfig(prev => ({
      ...prev,
      widgets: prev.widgets.filter(widget => widget.id !== id)
    }));
  }, [setConfig]);

    const raiseWidget = useCallback((id: string) => {
    if (config.widgets.findIndex((e) => e.id == id ) != (config.widgets.length-1))
      setConfig(prev => ({
        ...prev,
        widgets: [...prev.widgets.filter(widget => widget.id !== id), ...prev.widgets.filter(widget => widget.id == id)]
      }));
  }, [setConfig]);

  const addWidget = useCallback((type: string) => {
    const newWidget: WidgetInterface = {
      id: Date.now().toString(),
      type: type,
      position: { x: 100 + Math.random() * 200, y: 100 + Math.random() * 200 },
      size: { width: 300, height: 200 },
      title: type.charAt(0).toUpperCase() + type.slice(1),
    };

    setConfig(prev => ({
      ...prev,
      widgets: [...prev.widgets, newWidget]
    }));
  }, [setConfig]);

  const forceWidgetsToScreen = () => {
    setConfig( prev => ({
      ...prev,
      widgets: prev.widgets.map((w) => {w.position=forceToWidgetScreen(w.position);return w;})
    })

    )
  }

  const updateBackground = useCallback((background: HomeScreenConfig['background']) => {
    setConfig(prev => ({
      ...prev,
      background
    }));
  }, [setConfig]);

  const updateTextColor = useCallback((textColor: HomeScreenConfig['textColor']) => {
    setConfig(prev => ({
      ...prev,
      textColor
    }));
  }, [setConfig]);

  const updateSnapValue = useCallback((snapValue: number) => {
    setConfig(prev => ({
      ...prev,
      snapValue
    }));
  }, [setConfig]);

  const updateJitterFactor = useCallback((antiBurnJitter: number) => {
    setConfig(prev => ({
      ...prev,
      antiBurnJitter
    }));
  }, [setConfig]);

    const updateStageSettings = useCallback((stages: number, stageSwitchTime : number) => {
    setConfig(prev => ({
      ...prev,
      stages,
      stageSwitchTime
    }));
  }, [setConfig]);

  const getBackgroundStyle = () => {
    const { background } = config;
    switch (background.type) {
      case 'gradient':
        return { background: background.value };
      case 'color':
        return { backgroundColor: background.value };
      case 'image':
        return {
          backgroundImage: `url(${background.value})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        };
      default:
        return {};
    }
  };


  const getStyleSheet = () => {
    const { textColor, background } = config;
    const isWhite = (textColor.color == "#fff" || textColor.color == "#ffffff");
    return " :root {--text-color: "+textColor.color+";\n --secondary-text-color: "+textColor.secondaryColor+";\n --background-color: "+(background.type == "color" ? background.value : "#ffffff")+"; --red-text-color: "+(isWhite ? "#ff3333": "#880000")+"; --orange-text-color: "+(isWhite ? "#ffaa33": "#dd6600")+"; --yellow-text-color: "+(isWhite ? "#ffff66": "#888800")+"; --green-text-color: "+(isWhite ? "#66ff66": "#008800")+"; }"
  }


  function getWidget(widgetType : string, editMode : boolean, widget : WidgetInterface) {
    switch  (widgetType) {
      case "marquee":
        return (<MarqueeWidget><span>Flavoria ruokalinjalinjastossa suurin ruuhka-aika keskimäärin on kello 11.30. Linja 2B on selkeästi käytetyin linjasto.<span style={{padding: "20px"}}></span> On Average peak traffic hour in Flavoria is at 11:30. Line 2B is clearly the most used lunch line.</span></MarqueeWidget>)
      case "traffic-graph":
        return (<TrafficGraph color={config.textColor.color} />)
      case "logo":
        return (<LogoWidget />)
      case "map":
        return (<MapWidget editable={editMode} widget={widget} onUpdate={updateWidget} color={config.textColor.color} colorSecondary={config.background.type == "color" ? config.background.value : "transparent"} />)
      case "clock":
        return (<ClockWidget />)
      case "food":
        return (<FoodWidget widget={widget} onUpdate={updateWidget} editable={editMode} />)
      case "vertical-separator":
        return (<VerticalSeparator />)
      case "horizontal-separator":
        return (<HorizontalSeparator />)
      case "title":
        return (<TitleWidget widget={widget} onUpdate={updateWidget} editable={editMode} />)
      case "traffic":
        return(<>
          <TrafficStatus editMode={editMode} widget={widget} onUpdate={updateWidget} />
        </>)
    }
  }
    
  return (
    <div 
      className="app-container"
      style={getBackgroundStyle()}
      onContextMenu={(e) => {
        e.preventDefault(); 
        e.stopPropagation();
        setShowContextMenu(true); 
        let posY = window.innerHeight-(contextMenuRef.current?.offsetHeight ?? contextMenuWidth);
        posY = posY < 0 ? 0 : posY;
        setContextMenuPosition({x: Math.min(Math.max(0, e.clientX), window.innerWidth-314), y: Math.min(Math.max(0, e.clientY), posY)});
        }
      }
    >
      {/* Dynamic Style Sheet */}
      <style>
        {getStyleSheet()}
      </style>
      
      {/* Welcome message on empty board */}
      {config.widgets.length === 0 && (
        <div className="welcome-container">
          <div className="welcome-content">
            <h1 className="welcome-title">Welcome to Flavoria Data Visualizer</h1>
            <p className="welcome-subtitle">Right click to add widgets</p>
          </div>
        </div>
      )}

      {/* The widgets themselves */}
      {config.widgets.map(widget => (
        <Widget
          snapValue={config.snapValue}
          currentStage={stage}
          noStages={config.stages}
          textColor={config.textColor.color}
          editable={editMode}
          key={widget.id}
          widget={widget}
          onUpdate={updateWidget}
          onDelete={deleteWidget}
          onRaise ={raiseWidget}
          jitterX={screenAntiBurnJitter.x*config.antiBurnJitter}
          jitterY={screenAntiBurnJitter.y*config.antiBurnJitter}
        >
          { getWidget(widget.type, editMode, widget) }
        </Widget>
      ))}

      {/* Settings Panel */}
      <SettingsPanel
        jitterFactor={config.antiBurnJitter}
        snapValue={config.snapValue}
        stageSwitchTime={config.stageSwitchTime}
        totalNumberOfStages={config.stages}
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        currentBackground={config.background}
        currentTextColor={config.textColor}
        currentStage={stage}
        onBackgroundChange={updateBackground}
        onTextColorChange={updateTextColor}
        onSnapValueChange={updateSnapValue}
        onAntiBurnJitterChange={updateJitterFactor}
        onStageSettingsChange={updateStageSettings}
        onStageChange={(i) => {setStage(i)}}
      />

      {/* Right Click Context Menu */}
      {showContextMenu && (
          <div ref={contextMenuRef} className="context-menu" style={{left: contextMenuPosition.x, top: contextMenuPosition.y}}>
            { /* Widgets */ }

            <button onClick={() => {
                addWidget("clock")
              }} className="context-menu-item" >
              <Clock size={12} /> Add Clock
            </button>

              <button onClick={() => {
                addWidget("marquee")
              }} className="context-menu-item" >
              <ScrollText size={12} /> Add Marquee
            </button>

            <button onClick={() => {
                addWidget("traffic")
              }} className="context-menu-item" >
              <ArrowBigRight size={12} /> Add Traffic Indicator
            </button>

            <button onClick={() => {
                addWidget("traffic-graph")
              }} className="context-menu-item" >
              <BarChart2 size={12} /> Add Traffic Graph
            </button>

            <button onClick={() => {
                addWidget("food")
              }} className="context-menu-item" >
              <Hamburger size={12} /> Add Food Menu
            </button>

            <button onClick={() => {
                addWidget("logo")
              }} className="context-menu-item" >
              <Pentagon size={12} /> Add Logo
            </button>

            <button onClick={() => {
                addWidget("map")
              }} className="context-menu-item" >
              <MapIcon size={12} /> Add Map
            </button>

              <button onClick={() => {
                addWidget("title")
              }} className="context-menu-item" >
              <LetterText size={12} /> Add Title
            </button>


            <hr className='context-menu-separator'/>

            {/* Separators */}
            <button onClick={() => {
                addWidget("vertical-separator")
              }} className="context-menu-item" >
              <FoldHorizontal size={12} /> Add Vertical Separator
            </button>

            <button onClick={() => {
                addWidget("horizontal-separator")
              }} className="context-menu-item" >
              <FoldVertical size={12} /> Add Horizontal Separator
            </button>

            <hr className='context-menu-separator'/>

            {/* Options */}
            <button onClick={() => {
                setEditMode(!editMode);
              }} className="context-menu-item" >
              <Edit size={12} /> Toggle Edit Mode (Ctrl + E)
            </button>
            <button onClick={() => {
                forceWidgetsToScreen();
              }} className="context-menu-item" >
              <Shrink size={12} /> Move widgets back to window area
            </button>
            <button onClick={() => {
                setSettingsOpen(true);
              }} className="context-menu-item" >
              <Settings size={12} /> Settings
            </button>
          </div>
        )}
    </div>
  );
}

export default App;